// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState } from 'react';

import type { WorkOrder, WorkOrderItem } from '@evolvemep/api-ts';
import { Menu, Button, Flex, type MantineSize } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isNotNil } from 'helpers/isNotNil';
import { AddTasksToBOPModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/AddTasksToBOPModal';
import { AddTasksToItemsModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/AddTasksToItemsModal';
import { InheritBOPModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/InheritBOPModal';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = {
  workOrder: WorkOrder;
  parentWorkOrderItem?: WorkOrderItem;
  onTasksUpdated: () => void;
  onTaskAttachmentsChanged?: () => void;
  size?: MantineSize;
};

export const AddTasksMenu = ({
  workOrder,
  parentWorkOrderItem,
  onTasksUpdated,
  onTaskAttachmentsChanged,
  size = 'xs',
}: Props) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);

  const [menuItemSelected, setMenuItemSelected] = useState<'task-list' | 'catalog-assembly' | null>(null);

  if (!canEditWorkOrder('editTasks')) return null;

  return (
    <Flex>
      <Menu position="bottom-end">
        <Menu.Target>
          <Button size={size} variant="light">
            Add tasks
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<EvolveIcon icon="NewTask" />} onClick={() => setMenuItemSelected('task-list')}>
            From task list
          </Menu.Item>
          <Menu.Item
            leftSection={<EvolveIcon icon="Catalog" />}
            onClick={() => setMenuItemSelected('catalog-assembly')}
          >
            From catalog assembly
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {isNotNil(parentWorkOrderItem) ? (
        <AddTasksToBOPModal
          opened={menuItemSelected === 'task-list'}
          onClose={() => setMenuItemSelected(null)}
          workOrder={workOrder}
          workOrderItem={parentWorkOrderItem}
          onTasksUpdated={onTasksUpdated}
          onTaskAttachmentsChanged={onTaskAttachmentsChanged}
        />
      ) : (
        <AddTasksToItemsModal
          opened={menuItemSelected === 'task-list'}
          onClose={() => setMenuItemSelected(null)}
          workOrder={workOrder}
          onTasksAdded={onTasksUpdated}
        />
      )}
      <InheritBOPModal
        opened={menuItemSelected === 'catalog-assembly'}
        onClose={() => setMenuItemSelected(null)}
        workOrder={workOrder}
        workOrderItem={parentWorkOrderItem}
        onBillOfProcessAdded={onTasksUpdated}
      />
    </Flex>
  );
};
